import type {
  Applicant,
  ApplicantFilters,
  ApplicantListResponse,
  FilterOptionsResponse,
} from '@/types/applicant';
import type {
  TalentPoolCandidateFilters,
  TalentPoolCandidateListResponse,
} from '@/types/talent-pool-candidate';
import { handleAPIResponse, getAuthHeaders } from '@/utils/apiUtils';

const API_BASE_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

/**
 * Converts filter object to URL parameters
 * Handles arrays by joining with commas
 */
const buildQueryParams = (filters: Partial<ApplicantFilters>): URLSearchParams => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        // Join array values with commas for multi-select filters
        if (value.length > 0) {
          params.append(key, value.join(','));
        }
      } else {
        params.append(key, value.toString());
      }
    }
  });

  return params;
};

export const applicantsApi = {
  /**
   * Upload applicants from files
   */
  upload: async (files: File[], source: string, subsource: string) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('source', source);
    formData.append('subsource', subsource);

    const response = await fetch(`${API_BASE_URL}/applicants/upload`, {
      method: 'POST',
      headers: getAuthHeaders({ addContentType: false }),
      body: formData,
    });
    return handleAPIResponse<{ applicants: Applicant[] }>(response);
  },

  /**
   * Update an applicant's basic information (name, phone, countryCode, email)
   */
  updateApplicant: async (
    id: string,
    data: {
      name?: string;
      phone?: string;
      countryCode?: string;
      email?: string;
    }
  ) => {
    const response = await fetch(`${API_BASE_URL}/applicants/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleAPIResponse<{ applicant: Applicant }>(response);
  },

  /**
   * Get filter options for applicants
   */
  getFilters: async () => {
    const response = await fetch(`${API_BASE_URL}/applicants/filters`, {
      headers: getAuthHeaders(),
    });
    return handleAPIResponse<FilterOptionsResponse>(response);
  },

  /**
   * List applicants with filters and pagination
   */
  list: async (
    page = 1,
    limit = 10,
    searchQuery = '',
    designation = '',
    additionalFilters: Record<string, any> = {}
  ) => {
    // Build base filters
    const filters: Partial<ApplicantFilters> = {
      page,
      limit,
      ...additionalFilters,
    };

    // Add search if provided
    if (searchQuery) {
      filters.search = searchQuery;
    }

    // Add designation if provided
    if (designation && designation !== 'all') {
      filters.designation = [designation];
    }

    // Build query string
    const queryParams = buildQueryParams(filters);
    const url = `${API_BASE_URL}/applicants?${queryParams.toString()}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<ApplicantListResponse>(response);
  },

  /**
   * Get applicant processing status
   */
  getStatus: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/applicants/${id}/status`, {
      headers: getAuthHeaders(),
    });
    return handleAPIResponse<{ processingStatus: Applicant['processingStatus'] }>(response);
  },

  /**
   * Send invites to applicants
   */
  sendInvites: async (
    applicantIds: string[],
    jobId: string,
    selectAll = false,
    filters?: Partial<ApplicantFilters>
  ) => {
    // Build the request body - when selectAll is true, use filters instead of applicantIds
    const requestBody: any = {
      jobId,
      selectAll,
    };

    // Only include applicantIds when not selecting all
    if (!selectAll && applicantIds.length > 0) {
      requestBody.applicantIds = applicantIds;
    }

    // Include filters when provided (typically when selectAll is true)
    if (filters && Object.keys(filters).length > 0) {
      requestBody.filters = filters;
    }

    const response = await fetch(`${API_BASE_URL}/applicants/invite`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });
    return handleAPIResponse<{ success: boolean; message: string }>(response);
  },
};

export const talentPoolCandidatesApi = {
  list: async (
    page = 1,
    limit = 10,
    filters: Partial<TalentPoolCandidateFilters> = {}
  ): Promise<TalentPoolCandidateListResponse> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters.search) params.append('search', filters.search);
    if (filters.experienceMin !== undefined) params.append('experienceMin', filters.experienceMin.toString());
    if (filters.experienceMax !== undefined) params.append('experienceMax', filters.experienceMax.toString());
    if (filters.jobIds?.length) params.append('jobIds', filters.jobIds.join(','));
    if (filters.company) params.append('company', filters.company);
    if (filters.designation) params.append('designation', filters.designation);

    const response = await fetch(`${API_BASE_URL}/applicants/candidates?${params.toString()}`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<TalentPoolCandidateListResponse>(response);
  },
};
